import type { BadgeVariant } from "@design";
import { useTranslation } from "react-i18next";
import type { ExtensionResponse, ExtensionSummary, SetupIssue, SetupStatus } from "@/api";

export type SetupState = SetupStatus["state"];

export const STATE_BADGE: Record<SetupState, BadgeVariant> = {
  ready: "success",
  needs_setup: "warning",
  unavailable: "neutral",
  off: "neutral",
};

/** Built-ins (platform capabilities) list after integrations (docs/design/web/WEB_EXTENSIONS_DISCOVERY.md D22). */
export function isBuiltIn(extension: ExtensionSummary): boolean {
  return extension.setup?.kind === "built_in";
}

export function stateOf(extension: ExtensionSummary): SetupState {
  return extension.setup?.state ?? (extension.isEnabled ? "ready" : "off");
}

/** A credential key's label from the extension's field schema, else the key. */
export function credentialLabel(extension: ExtensionResponse | undefined, key: string): string {
  const fields = (extension?.credentialsConfig?.fields ?? []) as Array<{ key?: string; label?: string }>;
  return fields.find((f) => f.key === key)?.label ?? key;
}

/** One sentence per setup issue, translated from its code. */
export function useIssueText() {
  const { t } = useTranslation("adminIntegrations");
  return (issue: SetupIssue, extension?: ExtensionResponse) =>
    issue.code === "MISSING_CREDENTIAL"
      ? t("issues.MISSING_CREDENTIAL", { field: issue.label ?? credentialLabel(extension, issue.key ?? "") })
      : t("issues.NOT_AVAILABLE_IN_DEPLOYMENT");
}
